import type { ColumnDef, SortingState } from '@tanstack/react-table';
import type { AuthMeResponse } from 'api/auth/Auth.schemas.ts';
import type { StatControllerListResponse } from 'api/stat/Stat.schema.ts';
import cx from 'clsx';
import dayjs from 'common/dayjs.ts';
import { formatShortAddress, fromQuerystring, GET, getLegStatusColor, getRuntimeTimezone, isGranted, POST, parseFormValues, toQuerystring } from 'common/helpers.ts';
import { isEqual } from 'es-toolkit';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { LEG_STATUS, Leg } from 'types/Leg.ts';
import { LEG_STATUSES } from 'types/Leg.ts';
import { Button } from 'ui/component/Button.tsx';
import { IconBadge } from 'ui/component/IconBadge.tsx';
import { IClock, IFilters, ISearch, ITruck } from 'ui/component/Icons.tsx';
import { RowSelection } from 'ui/component/RowSelection.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Stepper } from 'ui/component/Stepper.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { Toggle } from 'ui/control/Toggle.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import { useSessionPersistedFilters } from 'ui/hook/useSessionPersistedFilters.ts';
import { BulkSupplierAssignmentDialog } from '#admin/component/BulkSupplierAssignmentDialog.tsx';
import { Datetime } from '#admin/component/Datetime.tsx';
import { DriverAssignmentDialog } from '#admin/component/DriverAssignmentDialog.tsx';
import { getSWRKeyForPage, Table, type TableRef } from '#admin/component/table/index.ts';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

// The next actionable step on a leg: collection while pending, delivery while transiting, nothing once delivered/cancelled.
// Used to emphasise the relevant address + ETA columns so the actionable side stands out.
const getNextStep = (status: LEG_STATUS): 'collection' | 'delivery' | null => {
  if (status === 'pending') return 'collection';
  if (status === 'transiting') return 'delivery';
  return null;
};

// A filter toggle is "on" only when truthy; any off value (boolean false, persisted 'false', or '') normalizes to ''.
// parseFormValues drops '' from the request, so an off toggle behaves identically to Clear filters — the param is omitted,
// never sent as `=false` (which the API would treat as an actual filter, e.g. delayed=false -> only on-time legs).
const toToggleValue = (value: unknown): boolean | '' => (value === true || value === 'true' ? true : '');

export const getColumns = (me: AuthMeResponse | undefined, env: any, t: any, legs: Leg[] | undefined): ColumnDef<Leg>[] => {
  if (!me || !env || !legs) return [];

  const adminLayout = isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID);

  const columns: ColumnDef<Leg>[] = [
    {
      accessorKey: 'shipment_logistics_ex_id',
      header: t('labels.orderNumber'),
      size: 150,
      cell: (info) => {
        const value = info.getValue<string>();
        const leg = info.row.original;
        const now = dayjs();
        const nextETATime = !leg.origin_actual_datetime ? dayjs(leg.origin_expected_datetime) : dayjs(leg.destination_expected_datetime);
        const nextETA = nextETATime.diff(now, 'minutes');

        let is_delayed_tooltip = '';
        let is_delayed_color = '';
        if (nextETA < 0) {
          is_delayed_tooltip = t('tooltips.delayedShipment');
          is_delayed_color = 'text-red-dark';
        } else if (nextETA < 30) {
          is_delayed_tooltip = t('tooltips.nextETA', { minutes: nextETA });
          is_delayed_color = 'text-yellow';
        }

        return (
          <div className="flex flex-col gap-0.5">
            <Link className="anchor anchor-novisit flex flex-row items-center gap-1" to={`/shipment/${info.row.original.shipment_id}`}>
              {value}
              <div className="flex flex-row gap-0">{is_delayed_tooltip && <IClock className={is_delayed_color} title={is_delayed_tooltip} />}</div>
            </Link>
            {info.row.original.customer_name && <span className="text-grey-dark text-xs">{info.row.original.customer_name}</span>}
          </div>
        );
      },
    },
    {
      id: 'leg_number',
      header: t('labels.legNumber'),
      // wide enough for the in-transit truck badge to sit between the two waypoint circles without covering them
      size: 140,
      meta: { hideFallback: true },
      cell: (info) => {
        const leg = info.row.original;
        if (!leg.ordinal) return null; // Let fallback handle it

        return (
          <Stepper
            className="w-[100px]"
            steps={[String(leg.ordinal), String(leg.ordinal + 1)]}
            currentStep={leg.status === 'delivered' ? String(leg.ordinal + 1) : leg.status === 'transiting' ? String(leg.ordinal) : 'xyz'}
            currentLegStatus={leg.status}
            disableRing={true}
          />
        );
      },
    },
    ...(!adminLayout
      ? ([
          {
            header: 'Driver',
            // the value extracted with this accessorKey is then used on info.getValue() in the cell method
            accessorKey: 'driver',
            size: 150,
            meta: { fallback: t('views.legList.notAssigned'), fallbackClassName: 'text-red' },
            cell: (info) => {
              const value = info.getValue<Leg['driver']>();
              if (!value) return null;

              const { id, name: driverName } = value;
              return (
                <Link to={`/user/${id}`} className="anchor anchor-novisit">
                  {driverName}
                </Link>
              );
            },
          },
        ] as ColumnDef<Leg>[])
      : []),
    {
      accessorKey: 'origin_country',
      header: t('labels.from'),
      size: 170,
      cell: (info) => {
        const leg = info.row.original;
        const lines = formatShortAddress({ postcode: leg.origin_postcode, city: leg.origin_city, country: leg.origin_country });
        return (
          <span className={cx('flex w-max flex-col items-center gap-1', getNextStep(leg.status) === 'collection' && 'font-semibold')}>
            {lines.map((line, i) => (
              <span key={i} className={cx(i === 0 && 'self-start')}>
                {line}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      accessorKey: 'origin_expected_datetime',
      header: t('labels.collectionAt'),
      size: 210,
      cell: (info) => {
        const leg = info.row.original;
        return (
          <span className={cx(getNextStep(leg.status) === 'collection' && 'font-semibold')}>
            <Datetime timezone={leg.origin_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'} rangeFrom={leg.origin_expected_from_datetime}>
              {leg.origin_expected_datetime}
            </Datetime>
          </span>
        );
      },
    },
    /*
    {
      accessorKey: 'origin_actual_datetime',
      header: t('labels.actualCollectionAt'),
      size: 180,
      cell: (info) => {
        const leg = info.row.original;
        return (
          <Datetime timezone={leg.origin_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {leg.origin_actual_datetime}
          </Datetime>
        );
      },
    },
    */
    {
      accessorKey: 'destination_country',
      header: t('labels.to'),
      size: 170,
      cell: (info) => {
        const leg = info.row.original;
        const lines = formatShortAddress({ postcode: leg.destination_postcode, city: leg.destination_city, country: leg.destination_country });
        return (
          <span className={cx('flex w-max flex-col items-center gap-1', getNextStep(leg.status) === 'delivery' && 'font-semibold')}>
            {lines.map((line, i) => (
              <span key={i} className={cx(i === 0 && 'self-start')}>
                {line}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      accessorKey: 'destination_expected_datetime',
      header: t('labels.deliveryAt'),
      size: 210,
      cell: (info) => {
        const leg = info.row.original;
        return (
          <span className={cx(getNextStep(leg.status) === 'delivery' && 'font-semibold')}>
            <Datetime timezone={leg.destination_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'} rangeFrom={leg.destination_expected_from_datetime}>
              {leg.destination_expected_datetime}
            </Datetime>
          </span>
        );
      },
    },
    /*
    {
      accessorKey: 'destination_actual_datetime',
      header: t('labels.actualDestinationAt'),
      size: 180,
      cell: (info) => {
        const leg = info.row.original;
        return (
          <Datetime timezone={leg.origin_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {leg.destination_actual_datetime}
          </Datetime>
        );
      },
    },
    */
    ...(adminLayout
      ? [
          {
            accessorKey: 'supplier_id',
            header: t('components.leg.labels.supplier'),
            size: 100,
            meta: { truncate: true },
            cell: (info) => {
              return info.row.original.supplier?.name;
            },
          },
          {
            accessorKey: 'driver_id',
            header: t('components.leg.labels.driver'),
            size: 100,
            meta: { truncate: true },
            cell: (info) => {
              return info.row.original.driver?.name;
            },
          },
        ]
      : []),
    {
      accessorKey: 'status',
      header: t('labels.status'),
      size: 140,
      cell: (info) => {
        const value = info.getValue<LEG_STATUS>();
        const renderTag = (status: LEG_STATUS) => <Tag className={cx('!p-2', getLegStatusColor(status))}>{t(`status.${status}`)}</Tag>;
        return renderTag(value);
      },
    },
  ];

  return columns;
};

type FormValues = {
  driver_id: string;
  search: string;
  status$in: LEG_STATUS[];
  unassigned: boolean | '';
  delayed: boolean | '';
  origin_postcode$in: string[];
  destination_postcode$in: string[];
  origin_expected_datetime$from: string;
  origin_expected_datetime$to: string;
  destination_expected_datetime$from: string;
  destination_expected_datetime$to: string;
  origin_actual_datetime$from: string;
  origin_actual_datetime$to: string;
  destination_actual_datetime$from: string;
  destination_actual_datetime$to: string;
  next_eta$from: string;
  next_eta$to: string;
  with_driver: string;
  with_supplier: string;
  with_location: string;
  with_customers: string[];
};

const querystringParserOptions = { arrayKeys: ['status$in', 'with_customers', 'origin_postcode$in', 'destination_postcode$in'], excludeKeys: ['size'] };
// stable identity so SelectMulti's options memo doesn't recompute on every parent render
const NO_OPTIONS: string[] = [];

type Props = {
  driverId?: string;
};

export const LegList = ({ driverId }: Props) => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const tableRef = React.useRef<TableRef>(null);
  const [searchParams, setSearchParams] = useSessionPersistedFilters('atlas:legs-filters', !driverId);
  const scrollRef = useRestoreScrollPositionOnBack('legs-scroll');
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([
    { id: 'shipment_logistics_ex_id', desc: true },
    { id: 'origin_expected_datetime', desc: false },
    { id: 'logistics_ex_id', desc: true },
  ]);
  const [selectedLegs, setSelectedLegs] = React.useState<Leg[]>([]);
  const { me } = useMe();
  const env = useEnv();
  const [bulkAssignDriverDialog, setBulkAssignDriverDialog] = React.useState<boolean>(false);
  const [bulkAssignSupplierDialog, setBulkAssignSupplierDialog] = React.useState<boolean>(false);
  const defaultValues: FormValues = {
    driver_id: driverId || '',
    search: '',
    status$in: [],
    unassigned: '',
    delayed: '',
    origin_postcode$in: [],
    destination_postcode$in: [],
    origin_expected_datetime$from: '',
    origin_expected_datetime$to: '',
    destination_expected_datetime$from: '',
    destination_expected_datetime$to: '',
    origin_actual_datetime$from: '',
    origin_actual_datetime$to: '',
    destination_actual_datetime$from: '',
    destination_actual_datetime$to: '',
    next_eta$from: '',
    next_eta$to: '',
    with_supplier: '',
    with_driver: '',
    with_location: '',
    with_customers: [],
  };
  const searchValues = { ...defaultValues, ...fromQuerystring(searchParams, querystringParserOptions) };
  const defaultValuesWithSearchValues = { ...searchValues, unassigned: toToggleValue(searchValues.unassigned), delayed: toToggleValue(searchValues.delayed) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { register, handleSubmit, formState, reset, getValues, setValue } = formContext;

  // @damiano TODO: check why this is also affected by the status$in filter and not enforced by permissions?
  const { data: rowsCount } = useSWR(formState.isValid ? ['/leg/count', values, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/leg/list', 50, values, sorters, parseFormValues), POST, {
    revalidateFirstPage: false,
  });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);
  const legs: Leg[] = React.useMemo(() => swr.data?.flat() || [], [swr]);

  const { data: stats } = useSWR<StatControllerListResponse>(['/stat/list', { include: ['ACTIVE_LEGS', 'ETA_TODAY_TOMORROW_LEGS'] }], POST);

  // autocomplete for filters
  const { data: locations } = useSWR(['/leg/utils/assigned-locations'], GET, { fallbackData: [] });
  const {
    data: { suppliers, drivers },
  } = useSWR(['/leg/utils/assigned-users'], GET, { fallbackData: { suppliers: [], drivers: [] } });
  const { data: customers } = useSWR<{ id: string; name: string }[]>(['/shipment/assigned-customers'], GET, { fallbackData: [] });

  const columns = React.useMemo(() => getColumns(me, env, t, legs), [me, env, t, legs]);

  const toggleFilters = () => setShowFilters(!showFilters);

  const resetFilters = () => {
    reset(defaultValues);
    window.setTimeout(triggerSubmit, 50);
  };

  const setFilter = (name: keyof FormValues, value: any) => () => {
    resetFilters();
    setValue(name, value, { shouldDirty: true, shouldTouch: true });
    triggerSubmit();
  };

  const setTodayTomorrowEtaFilter = () => {
    resetFilters();
    setValue('next_eta$from', dayjs().format('YYYY-MM-DD'), { shouldDirty: true, shouldTouch: true });
    setValue('next_eta$to', dayjs().add(1, 'day').format('YYYY-MM-DD'), { shouldDirty: true, shouldTouch: true });
    triggerSubmit();
  };

  // wait next tick or URL will not be updated
  const triggerSubmit = React.useCallback(() => window.setTimeout(() => formRef.current?.requestSubmit(), 50), []);

  const onSubmit = async (values: FormValues) => {
    // an off toggle (false) must mean "no filter" — same as cleared ('') — so it is omitted from the request
    const normalized = { ...values, unassigned: toToggleValue(values.unassigned), delayed: toToggleValue(values.delayed) };
    setSearchParams(toQuerystring(normalized, querystringParserOptions), { replace: true });
    setValues(normalized);
    await swr.mutate();
  };

  // we need active filters: if filter is of type array then we count if having least 1 selection, otherwise we count if being not nullish
  const filtersCount = Object.values(values).filter((value) => (Array.isArray(value) ? value.length > 0 : value !== '' && value !== null)).length;

  const clearFiltersEnabled = formState.isDirty || !isEqual(defaultValues, getValues());

  const onRowSelect = React.useCallback((selectedRows: Leg[]) => {
    setSelectedLegs(selectedRows);
  }, []);

  const onBulkAssignDriver = React.useCallback(() => {
    setBulkAssignDriverDialog(true);
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectedLegs([]);
    tableRef.current?.clearSelection();
  }, []);

  const onBulkAssignDriverSave = React.useCallback(() => {
    setBulkAssignDriverDialog(false);
    clearSelection(); // Clear selection after successful assignment
    void swr.mutate();
  }, [swr, clearSelection]);

  const onBulkAssignSupplier = React.useCallback(() => {
    setBulkAssignSupplierDialog(true);
  }, []);

  const onBulkAssignSupplierSave = React.useCallback(() => {
    setBulkAssignSupplierDialog(false);
    clearSelection();
    void swr.mutate();
  }, [swr, clearSelection]);

  const allLegsSameSupplier = React.useMemo(() => {
    return selectedLegs.every((leg) => leg.supplier_id === selectedLegs[0].supplier_id);
  }, [selectedLegs]);

  const allLegsPending = React.useMemo(() => {
    return selectedLegs.every((leg) => leg.status === 'pending');
  }, [selectedLegs]);

  const allLegsUnassigned = React.useMemo(() => {
    return selectedLegs.every((leg) => !leg.driver_id);
  }, [selectedLegs]);

  const assignDriversEnabled = React.useMemo(() => {
    return allLegsSameSupplier && allLegsPending && allLegsUnassigned;
  }, [allLegsSameSupplier, allLegsPending, allLegsUnassigned]);

  const assignSuppliersEnabled = React.useMemo(() => {
    return allLegsPending;
  }, [allLegsPending]);

  const tooltipText = React.useMemo(() => {
    if (!allLegsSameSupplier) return t('views.legList.selection.allLegsSameSupplier');
    if (!allLegsPending) return t('views.legList.selection.allLegsPending');
    if (!allLegsUnassigned) return t('views.legList.selection.allLegsUnassigned');
    return null;
  }, [allLegsSameSupplier, allLegsPending, allLegsUnassigned, t]);

  const supplierTooltipText = React.useMemo(() => {
    if (!allLegsPending) return t('views.legList.selection.allLegsPendingSupplier');
    return null;
  }, [allLegsPending, t]);

  return (
    <main className="view-container">
      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('navigation.legs')}</h4>
      </section>

      <section className="mt-4 mb-4 flex h-16 flex-row gap-8">
        <button
          type="button"
          className="paper !p-3 flex flex-1 cursor-pointer select-none flex-row items-center justify-start gap-5"
          onClick={setFilter('status$in', ['pending', 'transiting'])}
        >
          <Tag className="blue !cursor-pointer !border-0 !p-2 self-center">
            <ITruck size={20} />
          </Tag>
          <div className="relative flex flex-row items-center gap-2">
            {stats === undefined && <Spinner size={24} />}
            {stats !== undefined && <h3 className="leading-[100%]">{stats.ACTIVE_LEGS}</h3>}
            <span>{t('views.legList.activeLegs')}</span>
          </div>
        </button>

        <button type="button" className="paper !p-3 flex flex-1 cursor-pointer select-none flex-row items-center justify-start gap-5" onClick={setTodayTomorrowEtaFilter}>
          <Tag className="green !cursor-pointer !border-0 !p-2 self-center">
            <IClock size={20} />
          </Tag>
          <div className="relative flex flex-row items-center gap-2">
            {stats === undefined && <Spinner size={24} />}
            {stats !== undefined && <h3 className="leading-[100%]">{stats.ETA_TODAY_TOMORROW_LEGS}</h3>}
            <span>{t('views.legList.legsTodayTomorrow')}</span>
          </div>
        </button>
      </section>

      <div className="mb-6 grid grid-cols-1 gap-8">
        <section className={cx('mt-4 bg-white p-8 pb-4', selectedLegs.length > 0 && 'mb-16')}>
          <FormProvider {...formContext}>
            <form id="search-form" ref={formRef} className="mb-4 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className="flex flex-row items-center gap-8">
                <input type="hidden" {...register('driver_id')} />

                <Input
                  className="mr-auto w-[360px]"
                  name={'search'}
                  placeholder={t('placeholders.searchLegs')}
                  required={true}
                  autoFocus={true}
                  RightIcon={ISearch}
                  autoComplete={'off'}
                  debouncedAutoSubmitForm={'#search-form'}
                  debouncedAutoSubmitTimeout={500}
                />

                <Toggle name={'unassigned'} label={t('filters.driverUnassigned')} autoSubmitForm={'#search-form'} />

                <Toggle name={'delayed'} label={t('filters.delayed')} autoSubmitForm={'#search-form'} />

                <IconBadge icon={<IFilters className="size-[24px] fill-gray-400" />} badge={filtersCount || null} onClick={toggleFilters} />
              </div>

              {/* expanded filters section */}
              <div className={cx('filters-box', !showFilters && '!hidden')}>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-6">
                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.status')}
                    name={'status$in'}
                    options={LEG_STATUSES.reduce((acc, status) => ({ ...acc, [status]: t(`status.${status}`) }), {})}
                    selectable={'many'}
                    disableSearch={true}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  {me.group_role !== 'SUPPLIER' && (
                    <SelectMulti
                      className="col-span-1"
                      label={t('filters.supplier')}
                      name={'with_supplier'}
                      options={new Map(suppliers.map((supplier) => [supplier.id, supplier.name]))}
                      selectable={'one'}
                      closeOnChangedValue={true}
                      required={false}
                      hasClearAll={true}
                    />
                  )}

                  <SelectMulti
                    className="col-span-1"
                    label={t('filters.driver')}
                    name={'with_driver'}
                    options={new Map(drivers.map((driver) => [driver.id, driver.name]))}
                    selectable={'one'}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-1"
                    label={t('filters.location')}
                    name={'with_location'}
                    options={locations}
                    selectable={'one'}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.client')}
                    name={'with_customers'}
                    options={new Map((customers || []).map((c) => [c.id, c.name]))}
                    selectable={'many'}
                    closeOnChangedValue={false}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.collectionZip')}
                    name={'origin_postcode$in'}
                    options={NO_OPTIONS}
                    selectable={'many'}
                    dynamic={true}
                    placeholder={t('placeholders.typeZip')}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.deliveryZip')}
                    name={'destination_postcode$in'}
                    options={NO_OPTIONS}
                    selectable={'many'}
                    dynamic={true}
                    placeholder={t('placeholders.typeZip')}
                    required={false}
                    hasClearAll={true}
                  />

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.originExpectedAt')}</span>
                    <div className="control-date-range">
                      <Input name={'origin_expected_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'origin_expected_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.destinationExpectedAt')}</span>
                    <div className="control-date-range">
                      <Input name={'destination_expected_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'destination_expected_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.actualCollectionAt')}</span>
                    <div className="control-date-range">
                      <Input name={'origin_actual_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'origin_actual_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.actualDeliveryAt')}</span>
                    <div className="control-date-range">
                      <Input name={'destination_actual_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'destination_actual_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.nextEta')}</span>
                    <div className="control-date-range">
                      <Input name={'next_eta$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'next_eta$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-row gap-4">
                  <Button className="blue-outlined h-max min-w-max max-w-max p-1" type="button" onClick={toggleFilters}>
                    {t('actions.close')}
                  </Button>
                  <Button className="blue-outlined ml-auto min-w-max max-w-max" type="button" onClick={resetFilters} disabled={!clearFiltersEnabled}>
                    {t('actions.clearFilters')}
                  </Button>
                  <Button className="blue-outlined min-w-max max-w-max" disabled={showFilters && !formState.isDirty} type="submit">
                    {t('actions.apply')}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>

          <Table
            ref={tableRef}
            scrollRef={scrollRef}
            className={cx('max-h-[calc(100vh-300px)]', !legs.length && 'min-h-[300px]')}
            columns={columns}
            swr={{ ...swr, setSize: setSizeInUrl }}
            pagination={true}
            rowsCount={rowsCount}
            defaultSorting={sorters}
            onColumnSort={setSorters}
            estimateSize={50}
            overscan={50}
            onRowSelect={onRowSelect}
          />

          <RowSelection label={t('views.legList.selection.numberSelected', { count: selectedLegs.length })} selectedItems={selectedLegs}>
            <div className="flex gap-2">
              <Button className="red" onClick={clearSelection} type="button">
                {t('labels.clearSelection')}
              </Button>
              <Tooltip text={supplierTooltipText}>
                <Button className="blue w-max" onClick={onBulkAssignSupplier} disabled={!assignSuppliersEnabled}>
                  Assign Supplier
                </Button>
              </Tooltip>
              <Tooltip text={tooltipText}>
                <Button className="blue w-max" onClick={onBulkAssignDriver} disabled={!assignDriversEnabled}>
                  Assign Driver
                </Button>
              </Tooltip>
            </div>
          </RowSelection>
        </section>
      </div>

      {bulkAssignSupplierDialog && (
        <BulkSupplierAssignmentDialog legs={selectedLegs} onSave={onBulkAssignSupplierSave} onClose={() => setBulkAssignSupplierDialog(false)} isOpen={bulkAssignSupplierDialog} />
      )}
      {bulkAssignDriverDialog && (
        <DriverAssignmentDialog legs={selectedLegs} onSave={onBulkAssignDriverSave} onClose={() => setBulkAssignDriverDialog(false)} isOpen={bulkAssignDriverDialog} />
      )}
    </main>
  );
};
